import { Close, SearchOutlined } from "@mui/icons-material";
import {
  Box,
  Container,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BaseSearchOption } from "./SearchDirectory";

export type DefaultSearchBarProps<SearchOption extends BaseSearchOption> = {
  query: string;
  setQuery: (query: string) => void;
  selected?: SearchOption;
  setSelected: (option?: SearchOption) => void;
  disabled?: boolean;
};

export function SelectedCard<SearchOption extends BaseSearchOption>({
  selected,
  setSelected,
  disabled,
}: {
  selected: SearchOption;
  setSelected: (option?: SearchOption) => void;
  disabled?: boolean;
}) {
  const hasEmail = selected?.data?.primary_email || selected?.data?.email || "";

  return (
    <Container disableGutters sx={{ mb: "1rem" }}>
      <Paper sx={{ border: "1px solid black", p: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selected?.data?.theme?.shortLogoUrl ? (
            <img
              src={selected.data.theme.shortLogoUrl}
              alt="logo"
              style={{ width: "5rem", marginRight: "1rem" }}
            />
          ) : null}
          <Box sx={{ flex: 1 }}>
            <Typography variant="inherit">{selected?.name}</Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: (theme) => theme.palette.grey[600] }}
            >
              {hasEmail ? hasEmail : "No email provided"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setSelected(undefined)}
            size="small"
            disabled={disabled}
          >
            <Close />
          </IconButton>
        </Box>
      </Paper>
    </Container>
  );
}

export function SearchBar({
  query,
  setQuery,
  disabled,
}: {
  query: string;
  setQuery: (query: string) => void;
  disabled?: boolean;
}) {
  return (
    <Container disableGutters>
      <TextField
        placeholder="Search Directory"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        sx={{ mb: "1rem" }}
        InputProps={{
          startAdornment: <SearchOutlined sx={{ mr: 1 }} />,
          endAdornment: query && (
            <Tooltip title="Clear Search">
              <IconButton onClick={() => setQuery("")} disabled={disabled}>
                <Close />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
    </Container>
  );
}

function DefaultSearchBar<SearchOption extends BaseSearchOption>({
  query,
  setQuery,
  selected,
  setSelected,
  disabled,
}: DefaultSearchBarProps<SearchOption>) {
  return selected ? (
    <SelectedCard
      selected={selected}
      setSelected={setSelected}
      disabled={disabled}
    />
  ) : (
    <SearchBar query={query} setQuery={setQuery} disabled={disabled} />
  );
}

export default DefaultSearchBar;
